import React, { useEffect, useState } from "react";
import { Star, Quote, Send, Loader2, CheckCircle2, BadgeCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fetchReviews, submitReview } from "../lib/api";

const Stars = ({ value, size = 16, className = "" }) => (
  <span className={`inline-flex ${className}`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= value ? "fill-[#F5A623] text-[#F5A623]" : "fill-transparent text-[#E0C9A6]"} />
    ))}
  </span>
);

const initials = (name = "") => name.trim().split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "G";

const ReviewsSection = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [baseLen, setBaseLen] = useState(0);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    fetchReviews()
      .then((data) => { setReviews(data); setBaseLen(data.length); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (user?.name) setName((n) => n || user.name); }, [user]);

  // Displayed social-proof summary (grows as new reviews are added this session).
  const BASE_RATING = 4.9;
  const BASE_COUNT = 480;
  const avg = BASE_RATING;
  const count = BASE_COUNT + Math.max(0, reviews.length - baseLen);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !message.trim() || rating < 1) {
      setError("Please add your name, a rating and a short review.");
      return;
    }
    setSubmitting(true);
    try {
      await submitReview({ name: name.trim(), rating, message: message.trim(), phone: user?.phone || "" });
      setDone(true);
      setMessage("");
      setRating(0);
      setTimeout(() => setDone(false), 6000);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not submit your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-[#FBF3EA] border-t border-[#EEDFC9]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="text-center mb-10">
          <span className="font-script text-2xl text-[#D46A8A]">Loved by our customers</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#3A2417] mt-1">Reviews & Ratings</h2>
          {count > 0 && (
            <div className="flex items-center justify-center gap-3 mt-4" data-testid="reviews-summary">
              <span className="font-display text-3xl font-bold text-[#3A2417]">{avg.toFixed(1)}</span>
              <div className="text-left">
                <Stars value={Math.round(avg)} size={18} />
                <div className="text-xs text-[#B07A5C] mt-0.5">Based on {count} review{count > 1 ? "s" : ""}</div>
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Existing reviews */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="py-16 grid place-items-center text-[#B07A5C]"><Loader2 size={26} className="animate-spin" /></div>
            ) : reviews.length === 0 ? (
              <p className="text-[#B07A5C] text-center py-16">No reviews yet — be the first to share your experience!</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5 max-h-[560px] overflow-y-auto pr-1" data-testid="reviews-list">
                {reviews.map((r) => (
                  <div key={r.id} className="bg-white rounded-2xl bakery-shadow p-5 relative" data-testid={`review-card-${r.id}`}>
                    <Quote size={30} className="text-[#F1DFC7] absolute top-4 right-4" />
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-[#E8A6B8] text-white grid place-items-center font-semibold text-sm">{initials(r.name)}</div>
                      <div>
                        <div className="font-semibold text-[#3A2417] text-sm flex items-center gap-1.5">
                          {r.name}
                          {r.verified && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#2A8C4A] bg-[#E7F3E0] rounded-full px-2 py-0.5" data-testid={`review-verified-${r.id}`} title="Verified buyer">
                              <BadgeCheck size={12} /> Verified
                            </span>
                          )}
                        </div>
                        <Stars value={r.rating} size={13} />
                      </div>
                    </div>
                    <p className="text-sm text-[#6B4226] leading-relaxed mt-3">{r.message}</p>
                    {r.created_at && (
                      <div className="text-[11px] text-[#B8977E] mt-3">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Write a review */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl bakery-shadow p-6 sticky top-24" data-testid="review-form">
              <h3 className="font-display text-xl font-bold text-[#3A2417]">Write a review</h3>
              <p className="text-sm text-[#B07A5C] mt-1">Tried our cakes? We'd love to hear from you.</p>

              {done ? (
                <div className="mt-6 text-center py-6" data-testid="review-success">
                  <CheckCircle2 size={44} className="text-[#25D366] mx-auto" />
                  <p className="text-[#3A2417] font-medium mt-3">Thank you for your review!</p>
                  <p className="text-sm text-[#B07A5C] mt-1">It will appear here once our team approves it.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#6B4226]">Your Rating</label>
                    <div className="flex gap-1 mt-1.5" data-testid="review-rating-picker">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <button
                          type="button"
                          key={i}
                          onClick={() => setRating(i)}
                          onMouseEnter={() => setHover(i)}
                          onMouseLeave={() => setHover(0)}
                          className="transition-transform hover:scale-110"
                          aria-label={`Rate ${i} star`}
                          data-testid={`review-star-${i}`}
                        >
                          <Star size={30} className={i <= (hover || rating) ? "fill-[#F5A623] text-[#F5A623]" : "fill-transparent text-[#E0C9A6]"} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#6B4226]">Name</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="w-full mt-1.5 h-11 px-4 rounded-xl bg-[#FBF3EA] border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A]"
                      data-testid="review-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#6B4226]">Review</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      placeholder="Share a few words about your experience..."
                      className="w-full mt-1.5 px-4 py-3 rounded-xl bg-[#FBF3EA] border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A] resize-none"
                      data-testid="review-message-input"
                    />
                  </div>
                  {error && <p className="text-sm text-red-600" data-testid="review-error">{error}</p>}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 rounded-full bg-[#D46A8A] text-white font-medium flex items-center justify-center gap-2 hover:bg-[#c25a7b] transition-colors disabled:opacity-60"
                    data-testid="review-submit-btn"
                  >
                    {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />} Submit Review
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
